import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Broadcast } from 'src/broadcast';
import { authQueries } from 'src/api/queries';
import { RequestsCountsResponse, RequestStatus } from 'src/api/services/RequestClient';
import { getObjectKeys } from 'src/helpers/getObjectKeys';
import { UseMakeQueryReturnType } from 'src/api/queries/useMakeQuery';
import { AdminViewedRequestsManager } from 'src/lib/utils/admin-viewed-requests';

let hookInitialized = false;

// TODO: static types
const useRequestsCounts = (
  countsQueryFn: UseMakeQueryReturnType<RequestsCountsResponse> | undefined,
) => {
  const client = useQueryClient();

  useEffect(() => {
    if (hookInitialized || !countsQueryFn) {
      return;
    }

    Broadcast.user.requestUpdatedByCustomer.setListener(({ id }) => {
      client.invalidateQueries({ queryKey: [authQueries.request, id] });
      client.invalidateQueries({ queryKey: [authQueries.requestOutcomes, id] });
      client.invalidateQueries({ queryKey: [authQueries.requestCounts] });

      countsQueryFn.refetch();
    });

    hookInitialized = true;
  }, []);

  if (!countsQueryFn) {
    return {
      counts: null,
      query: { isSuccess: false } as any,
      totalQueued: 0,
      newQueued: 0,
      totalEditing: 0,
      newEditing: 0,
      totalDelivered: 0,
      newDelivered: 0,
      totalComplete: 0,
      newComplete: 0,
      totalNew: 0,
      review: () => {},
      add: () => {},
      update: () => {},
      remove: () => {},
    };
  }

  const { data: counts, ...query } = countsQueryFn;

  // Filter counts for admin users based on localStorage
  const getFilteredCounts = (originalCounts: RequestsCountsResponse | undefined) => {
    if (!originalCounts || !AdminViewedRequestsManager.isCurrentUserAdmin()) {
      return originalCounts;
    }

    const filtered = { ...originalCounts };

    // Filter 'new' arrays for each status, excluding admin-viewed requests
    Object.keys(filtered).forEach((status) => {
      if (filtered[status as keyof RequestsCountsResponse]?.new) {
        filtered[status as keyof RequestsCountsResponse].new =
          AdminViewedRequestsManager.filterUnviewedRequestIds(
            filtered[status as keyof RequestsCountsResponse].new,
          );
      }
    });

    return filtered;
  };

  const filteredCounts = getFilteredCounts(counts);

  const totalQueued = filteredCounts?.queued.total.length ?? 0,
    newQueued = filteredCounts?.queued.new.length ?? 0,
    totalEditing = filteredCounts?.currently_editing.total.length ?? 0,
    newEditing = filteredCounts?.currently_editing.new.length ?? 0,
    totalDelivered = filteredCounts?.delivered.total.length ?? 0,
    newDelivered = filteredCounts?.delivered.new.length ?? 0,
    totalComplete = filteredCounts?.complete.total.length ?? 0,
    newComplete = filteredCounts?.complete.new.length ?? 0,
    totalNew = newQueued + newEditing + newDelivered + newComplete;

  const add = (id: string) => {
    query.setQueryData((prev) => {
      prev.queued.total.push(id);

      return prev;
    });
  };

  const update = (id: string, status: RequestStatus) => {
    query.setQueryData((prev) => {
      const foundInCounts = prev[status].total.find((r) => r === id);
      if (!foundInCounts) {
        remove(id);
        prev[status].total.push(id);
      }

      return prev;
    });
  };

  const remove = (id: string) => {
    query.setQueryData((prev) => {
      const statuses = getObjectKeys(prev);
      for (let i = 0; i < statuses.length; i++) {
        const status = statuses[i];
        const found = prev[status].total.find((r) => r === id);

        if (found) {
          prev[status].total = prev[status].total.filter((r) => r !== found);
          prev[status].new = prev[status].new.filter((r) => r !== found);

          break;
        }
      }

      return prev;
    });
  };

  const review = (id: string, status?: RequestStatus) => {
    if (AdminViewedRequestsManager.isCurrentUserAdmin()) {
      // For admin users: use localStorage tracking
      AdminViewedRequestsManager.markRequestAsViewed(id);
      // No need to modify query data as filtering is handled in getFilteredCounts
    } else {
      // For non-admin users: use existing logic (relies on backend reviewed_by_editor)
      query.setQueryData((prev) => {
        if (status) {
          prev[status].new = prev[status].new.filter((r) => r !== id);

          return prev;
        }

        const statuses = getObjectKeys(prev);
        for (let i = 0; i < statuses.length; i++) {
          const key = statuses[i];
          const oldCount = prev[key].new.length;
          if (!oldCount) {
            continue;
          }

          prev[key].new = prev[key].new.filter((r) => r !== id);
          const newCount = prev[key].new.length;
          if (oldCount > newCount) {
            break;
          }
        }

        return prev;
      });
    }

    // Invalidate requestsCounts and requests cache to ensure fresh data
    client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
    client.invalidateQueries({ queryKey: [authQueries.requests] });
  };

  return {
    counts: filteredCounts,
    query,
    totalQueued,
    newQueued,
    totalEditing,
    newEditing,
    totalDelivered,
    newDelivered,
    totalComplete,
    newComplete,
    totalNew,
    review,
    add,
    update,
    remove,
  };
};

export { useRequestsCounts };
